import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import * as XLSX from 'xlsx';
import ConfirmModal from '../components/ConfirmModal';

export default function AdminDashboard() {
  const { t, lang, user, api } = useApp();
  const navigate = useNavigate();
  const [tab, setTab] = useState('students');
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  // New student form
  const [newName, setNewName] = useState('');
  const [newNameJa, setNewNameJa] = useState('');
  const [newProgramIds, setNewProgramIds] = useState([]);
  const [newTeacherId, setNewTeacherId] = useState('');
  const [newStudentResult, setNewStudentResult] = useState(null);

  // Invite teacher form
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteResult, setInviteResult] = useState(null);
  // Settings
  const [certThreshold, setCertThreshold] = useState(87);
  const [settingsSaved, setSettingsSaved] = useState(false);
  // Edit modals
  const [editUser, setEditUser] = useState(null); // { id, name, name_ja, role }
  const [editMsg, setEditMsg] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null); // user to delete
  // Force certificate
  const [forcingCert, setForcingCert] = useState(null);
  const forceCertificate = async (student) => {
    const next = !student.certificate_unlocked;
    setForcingCert(student.id);
    try {
      await api('patch', `/admin/students/${student.id}/force-certificate`, { unlocked: next });
      loadAll();
    } catch {}
    setForcingCert(null);
  };

  // Rankings
  const [rankingStudents, setRankingStudents] = useState([]);
  const [rankingSelected, setRankingSelected] = useState(new Set());
  // 3a/3b = joint 3rd place (2 students allowed)
  const [rankingPicks, setRankingPicks] = useState({ 1: null, 2: null, '3a': null, '3b': null, 4: null, 5: null });
  const [scoreOverrides, setScoreOverrides] = useState({}); // { studentId: displayScore }
  const [rankingLoading, setRankingLoading] = useState(false);
  const [rankingSaved, setRankingSaved] = useState(false);
  // Breakdown modal
  const [breakdownStudent, setBreakdownStudent] = useState(null); // { id, name }
  const [breakdownData, setBreakdownData] = useState([]);
  const [breakdownLoading, setBreakdownLoading] = useState(false);

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/login'); return; }
    loadAll();
  }, [user]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [studRes, teachRes, statsRes, settRes] = await Promise.all([
        api('get', '/admin/students'),
        api('get', '/admin/teachers'),
        api('get', '/admin/stats'),
        api('get', '/admin/settings'),
      ]);
      setStudents(studRes.data);
      setTeachers(teachRes.data);
      setStats(statsRes.data);
      setPrograms(statsRes.data.programs || []);
      if (settRes.data?.certificate_threshold) setCertThreshold(parseInt(settRes.data.certificate_threshold));
    } catch {}
    setLoading(false);
  };

  const toggleAdminProgram = (id) => {
    setNewProgramIds(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
  };

  const createStudent = async (e) => {
    e.preventDefault();
    try {
      const res = await api('post', '/admin/students', {
        name: newName, name_ja: newNameJa,
        teacher_id: newTeacherId || undefined,
        program_ids: newProgramIds,
      });
      setNewStudentResult(res.data);
      setNewName(''); setNewNameJa(''); setNewProgramIds([]); setNewTeacherId('');
      loadAll();
    } catch (err) {
      setNewStudentResult({ error: err.response?.data?.message || 'Error' });
    }
  };

  const inviteTeacher = async (e) => {
    e.preventDefault();
    try {
      const res = await api('post', '/admin/invite-teacher', { email: inviteEmail });
      setInviteResult(res.data);
      setInviteEmail('');
    } catch (err) {
      setInviteResult({ error: err.response?.data?.message || 'Error' });
    }
  };

  const toggleUser = async (id) => {
    await api('patch', `/admin/users/${id}/toggle`);
    loadAll();
  };

  const saveEditUser = async () => {
    try {
      const endpoint = editUser.role === 'student' ? `/admin/students/${editUser.id}` : `/admin/teachers/${editUser.id}`;
      await api('patch', endpoint, { name: editUser.name, name_ja: editUser.name_ja });
      setEditMsg(lang === 'ja' ? '✅ 更新しました' : '✅ Updated!');
      setTimeout(() => { setEditUser(null); setEditMsg(''); }, 1200);
      loadAll();
    } catch { setEditMsg('❌ Error'); }
  };

  const deleteUser = async (u) => {
    setConfirmDelete(u);
  };

  const confirmDeleteUser = async () => {
    if (!confirmDelete) return;
    try {
      const endpoint = confirmDelete.role === 'student' ? `/admin/students/${confirmDelete.id}` : `/admin/teachers/${confirmDelete.id}`;
      await api('delete', endpoint);
      loadAll();
    } catch {}
    setConfirmDelete(null);
  };

  const saveSettings = async () => {
    try {
      await api('put', '/admin/settings', { certificate_threshold: certThreshold });
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 2500);
    } catch {}
  };

  const exportStudentsXlsx = () => {
    const rows = students.map(s => ({
      Name: s.name,
      'Name (JA)': s.name_ja || '',
      'Login Code': s.login_code,
      'Stamp Count': s.stamp_count || 0,
      'Certificate Unlocked': s.certificate_unlocked ? 'Yes' : 'No',
      Programs: (s.programs || []).map(p => p.name).join(', '),
      Teacher: s.teacher_name || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Students');
    XLSX.writeFile(wb, 'students.xlsx');
  };

  const openBreakdown = async (student) => {
    setBreakdownStudent(student);
    setBreakdownData([]);
    setBreakdownLoading(true);
    try {
      const res = await api('get', `/admin/students/${student.id}/breakdown`);
      setBreakdownData(res.data || []);
    } catch {}
    setBreakdownLoading(false);
  };

  const loadRankings = async () => {
    setRankingLoading(true);
    try {
      const res = await api('get', '/admin/rankings');
      const data = res.data || [];
      console.log('[Rankings] API response:', data);
      setRankingStudents(data);
      const sel = new Set(data.map(s => s.id));
      setRankingSelected(sel);
      const picks = { 1: null, 2: null, '3a': null, '3b': null, 4: null, 5: null };
      const hasSaved = data.some(s => s.rank_position >= 1 && s.rank_position <= 5);
      const ranked = data.filter(s => s.rank_position > 0);
      console.log('[Rankings] ranked students with scores:', ranked.map(s => `${s.name} → pos ${s.rank_position} | score=${s.score} | display_score=${s.display_score}`));
      // Pre-populate overrides for every student: saved display_score if exists, else calculated.
      const overrides = {};
      data.forEach(s => {
        overrides[s.id] = s.display_score != null ? s.display_score : s.score;
      });
      console.log('[Rankings] scoreOverrides loaded:', overrides);
      setScoreOverrides(overrides);
      if (hasSaved) {
        // 3rd place: up to 2 students share position 3
        const thirds = data.filter(s => s.rank_position === 3);
        thirds.forEach((s, i) => { picks[i === 0 ? '3a' : '3b'] = s.id; });
        [1, 2, 4, 5].forEach(pos => {
          const found = data.find(s => s.rank_position === pos);
          if (found) picks[pos] = found.id;
        });
      } else {
        // Auto-assign top 5 by score
        data.slice(0, 5).forEach((s, i) => {
          if (i < 2) picks[i + 1] = s.id;
          else if (i === 2) picks['3a'] = s.id;
          else picks[i + 1] = s.id;
        });
      }
      setRankingPicks(picks);
    } catch {}
    setRankingLoading(false);
  };

  const toggleRankingStudent = (id) => {
    setRankingSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        // clear any pick using this student
        setRankingPicks(p => {
          const np = { ...p };
          Object.keys(np).forEach(k => { if (np[k] === id) np[k] = null; });
          return np;
        });
      } else { next.add(id); }
      return next;
    });
  };

  const assignRank = (position, studentId) => {
    setRankingPicks(prev => {
      const next = { ...prev };
      // clear same student from other positions
      Object.keys(next).forEach(k => { if (next[k] === studentId) next[k] = null; });
      next[position] = studentId || null;
      return next;
    });
  };

  const saveRankings = async () => {
    const rankedEntries = Object.entries(rankingPicks)
      .filter(([, sid]) => sid !== null)
      .map(([pos, sid]) => ({
        student_id: sid,
        rank_position: pos === '3a' || pos === '3b' ? 3 : parseInt(pos),
        display_score: scoreOverrides[sid] != null ? parseInt(scoreOverrides[sid]) : null,
      }));
    const rankedIds = new Set(rankedEntries.map(r => r.student_id));
    // Also persist score overrides for non-ranked students (rank_position=0)
    const nonRankedEntries = Object.entries(scoreOverrides)
      .filter(([sid]) => !rankedIds.has(parseInt(sid)))
      .map(([sid, score]) => ({ student_id: parseInt(sid), rank_position: 0, display_score: parseInt(score) }));
    const rankings = [...rankedEntries, ...nonRankedEntries];
    try {
      console.log('[Save Rankings] sending:', rankings);
      await api('post', '/admin/rankings', { rankings });
      console.log('[Save Rankings] success');
      setRankingSaved(true);
      setTimeout(() => setRankingSaved(false), 2500);
      loadRankings();
    } catch {}
  };

  const TABS = [
    { key: 'students',    icon: '🎒', labelEn: 'Students',       labelJa: '生徒' },
    { key: 'teachers',   icon: '👩‍🏫', labelEn: 'Teachers',       labelJa: '先生' },
    { key: 'rankings',   icon: '🏆', labelEn: 'Rankings',        labelJa: 'ランキング' },
    { key: 'add-student', icon: '➕', labelEn: 'Add Student',     labelJa: '生徒追加' },
    { key: 'invite',     icon: '📧', labelEn: 'Invite Teacher',  labelJa: '先生招待' },
    { key: 'settings',   icon: '⚙️', labelEn: 'Settings',        labelJa: '設定' },
  ];

  return (
    <div className="summer-bg pb-5">
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #073B4C, #118AB2)', padding: '2.5rem 0' }}>
        <div className="container">
          <h2 style={{ color: 'white', fontWeight: 900, margin: 0 }}>⚙️ {t.adminDashboard}</h2>
          <div className="row g-3 mt-3">
            {[
              { icon: '👩‍🏫', val: stats.teachers, label: t.totalTeachers },
              { icon: '🎒', val: stats.students, label: t.totalStudents },
              { icon: '⭐', val: stats.stamps, label: t.totalStampsGiven },
            ].map((s, i) => (
              <div key={i} className="col-4">
                <div style={{ background: 'rgba(255,255,255,0.12)', borderRadius: 16, padding: '1rem', color: 'white', textAlign: 'center', backdropFilter: 'blur(10px)' }}>
                  <div style={{ fontSize: '1.5rem' }}>{s.icon}</div>
                  <div style={{ fontSize: '2rem', fontWeight: 900 }}>{s.val ?? '—'}</div>
                  <div style={{ fontSize: '0.7rem', opacity: 0.8, fontWeight: 600 }}>{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="container mt-4">
        {/* Tabs */}
        <div className="tab-nav mb-4">
          {TABS.map(tb => (
            <button key={tb.key} className={`tab-btn ${tab === tb.key ? 'active' : ''}`} onClick={() => {
              setTab(tb.key);
              if (tb.key === 'rankings') loadRankings();
            }}>
              {tb.icon} {lang === 'ja' ? tb.labelJa : tb.labelEn}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-5"><div style={{ fontSize: '2rem', animation: 'spin 1s linear infinite' }}>⭐</div></div>
        ) : (
          <>
            {/* Students list */}
            {tab === 'students' && (
              <div className="card-summer p-4">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: 8 }}>
                  <h5 style={{ fontWeight: 900, margin: 0 }}>🎒 {lang === 'ja' ? '生徒一覧' : 'All Students'} ({students.length})</h5>
                  <button onClick={exportStudentsXlsx} style={{ background: '#16A34A', color: 'white', border: 'none', borderRadius: 10, padding: '6px 16px', fontWeight: 800, fontSize: '0.8rem', cursor: 'pointer' }}>
                    📊 {lang === 'ja' ? 'Excelで出力' : 'Export XLSX'}
                  </button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {students.map(s => (
                    <div key={s.id} style={{ background: 'white', borderRadius: 14, padding: '0.85rem 1rem', border: '1.5px solid #F1F5F9', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 140 }}>
                        <div style={{ fontWeight: 800, fontSize: '0.95rem' }}>{s.name}</div>
                        {s.name_ja && <div style={{ fontSize: '0.78rem', color: 'var(--muted)', fontWeight: 600 }}>{s.name_ja}</div>}
                        <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 2 }}>{s.programs || '—'}</div>
                      </div>
                      <code style={{ background: '#FFF0E0', padding: '4px 12px', borderRadius: 8, fontWeight: 900, color: 'var(--orange)', fontSize: '1.1rem', letterSpacing: 3, flexShrink: 0 }}>
                        {s.login_code}
                      </code>
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
                        <a href={`/student/${s.profile_slug}`} style={{ background: '#F1F5F9', color: '#64748b', borderRadius: 8, padding: '5px 10px', fontWeight: 700, fontSize: '0.78rem', textDecoration: 'none' }}>👁</a>
                        <button onClick={() => toggleUser(s.id)} style={{ background: s.is_active ? '#DCFCE7' : '#FEE2E2', color: s.is_active ? '#16A34A' : '#DC2626', border: 'none', borderRadius: 8, padding: '5px 10px', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer' }}>
                          {s.is_active ? '✓' : '✗'}
                        </button>
                        <button onClick={() => setEditUser({ id: s.id, name: s.name, name_ja: s.name_ja || '', role: 'student' })}
                          style={{ background: '#ECFEFF', color: '#0891b2', border: '1.5px solid #A5F3FC', borderRadius: 8, padding: '5px 10px', fontWeight: 800, fontSize: '0.78rem', cursor: 'pointer' }}>✏️</button>
                        <button onClick={() => forceCertificate(s)} disabled={forcingCert === s.id}
                          title={s.certificate_unlocked ? 'Revoke forced certificate' : 'Force unlock certificate'}
                          style={{ background: s.certificate_unlocked ? '#DCFCE7' : '#FFF0E0', color: s.certificate_unlocked ? '#16A34A' : '#F59E0B', border: `1.5px solid ${s.certificate_unlocked ? '#86EFAC' : '#FDE68A'}`, borderRadius: 8, padding: '5px 10px', fontWeight: 800, fontSize: '0.78rem', cursor: 'pointer' }}>
                          {s.certificate_unlocked ? '🏆✓' : '🏆'}
                        </button>
                        <button onClick={() => deleteUser({ ...s, role: 'student' })}
                          style={{ background: '#FEF2F2', color: '#ef4444', border: '1.5px solid #FECACA', borderRadius: 8, padding: '5px 10px', fontWeight: 800, fontSize: '0.78rem', cursor: 'pointer' }}>🗑️</button>
                      </div>
                    </div>
                  ))}
                  {students.length === 0 && <div style={{ color: 'var(--muted)', fontWeight: 600 }}>{lang === 'ja' ? 'まだ生徒がいません' : 'No students yet'}</div>}
                </div>
              </div>
            )}

            {/* Teachers list */}
            {tab === 'teachers' && (
              <div className="card-summer p-4">
                <h5 style={{ fontWeight: 900, marginBottom: '1.5rem' }}>👩‍🏫 {lang === 'ja' ? '先生一覧' : 'All Teachers'} ({teachers.length})</h5>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {teachers.map(tc => (
                    <div key={tc.id} style={{ background: 'white', borderRadius: 14, padding: '0.85rem 1rem', border: '1.5px solid #F1F5F9', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg, #073B4C, #118AB2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', flexShrink: 0 }}>👩‍🏫</div>
                      <div style={{ flex: 1, minWidth: 120 }}>
                        <div style={{ fontWeight: 800 }}>{tc.name}</div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>{tc.email}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
                        <button onClick={() => toggleUser(tc.id)} style={{ background: tc.is_active ? '#DCFCE7' : '#FEE2E2', color: tc.is_active ? '#16A34A' : '#DC2626', border: 'none', borderRadius: 8, padding: '5px 12px', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer' }}>
                          {tc.is_active ? '✓ Active' : '✗ Off'}
                        </button>
                        <button onClick={() => setEditUser({ id: tc.id, name: tc.name, name_ja: tc.name_ja || '', role: 'teacher' })}
                          style={{ background: '#ECFEFF', color: '#0891b2', border: '1.5px solid #A5F3FC', borderRadius: 8, padding: '5px 10px', fontWeight: 800, fontSize: '0.78rem', cursor: 'pointer' }}>✏️</button>
                        <button onClick={() => deleteUser({ ...tc, role: 'teacher' })}
                          style={{ background: '#FEF2F2', color: '#ef4444', border: '1.5px solid #FECACA', borderRadius: 8, padding: '5px 10px', fontWeight: 800, fontSize: '0.78rem', cursor: 'pointer' }}>🗑️</button>
                      </div>
                    </div>
                  ))}
                  {teachers.length === 0 && <div style={{ color: 'var(--muted)', fontWeight: 600 }}>{lang === 'ja' ? 'まだ先生がいません' : 'No teachers yet'}</div>}
                </div>
              </div>
            )}

            {/* Rankings */}
            {tab === 'rankings' && (
              <div className="card-summer p-4">
                <h5 style={{ fontWeight: 900, marginBottom: '0.4rem' }}>🏆 {lang === 'ja' ? 'ランキング確定' : 'Finalize Rankings'}</h5>
                <p style={{ fontSize: '0.82rem', color: 'var(--muted)', fontWeight: 600, marginBottom: '1.5rem' }}>
                  {lang === 'ja'
                    ? 'スコアはスタンプ数(70%)＋プログラム数(15%)＋出席日数(15%)で計算されます。'
                    : 'Score = stamps (70%) + programs (15%) + attendance days (15%). Select students to include, then assign 🥇🥈🥉.'}
                </p>

                {rankingLoading ? (
                  <div className="text-center py-4" style={{ fontSize: '1.5rem' }}>⭐</div>
                ) : (
                  <>
                    {/* Student score table — sorted by effective score (override > calculated) */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: '1.5rem' }}>
                      {[...rankingStudents].sort((a, b) => {
                        const sa = scoreOverrides[a.id] != null ? scoreOverrides[a.id] : a.score;
                        const sb = scoreOverrides[b.id] != null ? scoreOverrides[b.id] : b.score;
                        return sb - sa;
                      }).map((s, idx) => {
                        const included = rankingSelected.has(s.id);
                        const assignedRank = Object.entries(rankingPicks).find(([, sid]) => sid === s.id)?.[0];
                        const medals = { '1': '🥇', '2': '🥈', '3a': '🥉', '3b': '🥉', '4': '4️⃣', '5': '5️⃣' };
                        return (
                          <div key={s.id} style={{
                            background: included ? 'white' : '#F8FAFC',
                            borderRadius: 14, padding: '0.85rem 1rem',
                            border: `2px solid ${included ? (assignedRank ? '#FFD700' : '#E2E8F0') : '#F1F5F9'}`,
                            display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                            opacity: included ? 1 : 0.5,
                            transition: 'all 0.15s',
                          }}>
                            {/* Rank number */}
                            <div style={{ width: 28, textAlign: 'center', fontWeight: 900, fontSize: '0.9rem', color: '#94a3b8', flexShrink: 0 }}>
                              {assignedRank ? medals[assignedRank] : `#${idx + 1}`}
                            </div>

                            {/* Name — click to see breakdown */}
                            <div style={{ flex: 1, minWidth: 120, cursor: 'pointer' }} onClick={() => openBreakdown(s)}>
                              <div style={{ fontWeight: 800, fontSize: '0.95rem', color: 'var(--accent, #0891b2)', textDecoration: 'underline dotted' }}>{s.name}</div>
                              {s.name_ja && <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>{s.name_ja}</div>}
                            </div>

                            {/* Score — editable for all students */}
                            <div style={{ flex: 1, minWidth: 140 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3, fontSize: '0.72rem', fontWeight: 700, color: '#64748b' }}>
                                <span>⭐ {s.total_stamps} stamps · 📚 {s.program_count} prog</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <input
                                    type="number" min={0} max={100}
                                    value={scoreOverrides[s.id] != null ? scoreOverrides[s.id] : s.score}
                                    onChange={e => {
                                      const val = parseInt(e.target.value) || 0;
                                      setScoreOverrides(prev => ({ ...prev, [s.id]: val }));
                                    }}
                                    style={{ width: 52, padding: '2px 4px', borderRadius: 6, border: `1.5px solid ${assignedRank ? '#FFD166' : '#E2E8F0'}`, fontWeight: 900, fontSize: '0.82rem', color: assignedRank ? 'var(--orange)' : '#64748b', textAlign: 'center' }}
                                  />
                                  <span style={{ fontWeight: 700, color: '#94a3b8', fontSize: '0.72rem' }}>pts</span>
                                </div>
                              </div>
                              <div style={{ height: 6, background: '#F1F5F9', borderRadius: 3, overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${scoreOverrides[s.id] != null ? scoreOverrides[s.id] : s.score}%`, background: 'linear-gradient(90deg, #FF6B35, #FFD166)', borderRadius: 3, transition: 'width 0.3s' }} />
                              </div>
                            </div>

                            {/* Rank picker (only for included students) */}
                            {included && (
                              <div style={{ display: 'flex', gap: 4, flexShrink: 0, flexWrap: 'wrap' }}>
                                {[1, 2, '3a', '3b', 4, 5].map(pos => {
                                  const labels = { 1: '🥇', 2: '🥈', '3a': '🥉A', '3b': '🥉B', 4: '4th', 5: '5th' };
                                  const taken = rankingPicks[pos] !== null && rankingPicks[pos] !== s.id;
                                  const active = rankingPicks[pos] === s.id;
                                  return (
                                    <button key={pos} onClick={() => assignRank(pos, active ? null : s.id)}
                                      disabled={taken}
                                      style={{
                                        background: active ? '#FFF0E0' : '#F8FAFC',
                                        color: active ? 'var(--orange)' : taken ? '#CBD5E1' : '#64748b',
                                        border: `2px solid ${active ? 'var(--orange)' : '#E2E8F0'}`,
                                        borderRadius: 8, padding: '3px 7px', fontWeight: 900,
                                        fontSize: '0.75rem', cursor: taken ? 'not-allowed' : 'pointer',
                                      }}>{labels[pos]}</button>
                                  );
                                })}
                              </div>
                            )}

                            {/* Include/exclude toggle */}
                            <button onClick={() => toggleRankingStudent(s.id)}
                              style={{ background: included ? '#DCFCE7' : '#F1F5F9', color: included ? '#16A34A' : '#94a3b8', border: 'none', borderRadius: 8, padding: '5px 10px', fontWeight: 800, fontSize: '0.78rem', cursor: 'pointer', flexShrink: 0 }}>
                              {included ? '✓' : '+'}
                            </button>
                          </div>
                        );
                      })}
                      {rankingStudents.length === 0 && (
                        <div style={{ color: 'var(--muted)', fontWeight: 600, textAlign: 'center', padding: '2rem' }}>
                          {lang === 'ja' ? '生徒がいません' : 'No students found'}
                        </div>
                      )}
                    </div>

                    {/* Summary of picks */}
                    <div style={{ background: '#F8FAFC', borderRadius: 14, padding: '1rem 1.25rem', marginBottom: '1rem', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      {[
                        { key: 1,    icon: '🥇', label: '1st' },
                        { key: 2,    icon: '🥈', label: '2nd' },
                        { key: '3a', icon: '🥉', label: '3rd' },
                        { key: '3b', icon: '🥉', label: '3rd' },
                        { key: 4,    icon: '4️⃣', label: '4th' },
                        { key: 5,    icon: '5️⃣', label: '5th' },
                      ].map(({ key, icon, label }) => {
                        const s = rankingStudents.find(x => x.id === rankingPicks[key]);
                        return (
                          <div key={key} style={{ flex: 1, minWidth: 80, textAlign: 'center' }}>
                            <div style={{ fontSize: '1.5rem' }}>{icon}</div>
                            <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#94a3b8', marginBottom: 2 }}>{label}</div>
                            <div style={{ fontWeight: 800, fontSize: '0.78rem', color: s ? '#1e293b' : '#CBD5E1' }}>
                              {s ? s.name : (lang === 'ja' ? '未選択' : 'Not set')}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <button onClick={saveRankings} className="btn-summer w-100" style={{ marginBottom: 8 }}
                      disabled={Object.values(rankingPicks).filter(Boolean).length === 0}>
                      {rankingSaved ? '✅ Saved!' : (lang === 'ja' ? '💾 ランキングを確定' : '💾 Save Rankings')}
                    </button>
                    <div style={{ fontSize: '0.75rem', color: 'var(--muted)', fontWeight: 600, textAlign: 'center' }}>
                      {lang === 'ja' ? '保存すると生徒のプロフィールに表示されます' : 'Saved rankings will be visible on student profiles'}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Add student */}
            {tab === 'add-student' && (
              <div className="card-summer p-4" style={{ maxWidth: 500 }}>
                <h5 style={{ fontWeight: 900, marginBottom: '1.5rem' }}>➕ {t.addStudent}</h5>
                <form onSubmit={createStudent}>
                  <div className="mb-3">
                    <label style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--muted)', display: 'block', marginBottom: '0.4rem' }}>Name (English)</label>
                    <input type="text" className="input-summer" value={newName} onChange={e => setNewName(e.target.value)} required />
                  </div>
                  <div className="mb-3">
                    <label style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--muted)', display: 'block', marginBottom: '0.4rem' }}>名前（日本語）</label>
                    <input type="text" className="input-summer" value={newNameJa} onChange={e => setNewNameJa(e.target.value)} />
                  </div>
                  <div className="mb-3">
                    <label style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--muted)', display: 'block', marginBottom: '0.6rem' }}>
                      {lang === 'ja' ? 'プログラム（複数選択可）' : 'Programs (select 1–6)'}
                    </label>
                    {/* Select All / Deselect All */}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                      <button type="button"
                        onClick={() => setNewProgramIds(programs.map(p => p.id))}
                        style={{ background: '#FFF0E0', color: 'var(--orange)', border: '2px solid var(--orange)', borderRadius: 10, padding: '4px 14px', fontWeight: 800, fontSize: '0.78rem', cursor: 'pointer' }}
                      >
                        ✅ {lang === 'ja' ? '全て選択' : 'Select All'}
                      </button>
                      <button type="button"
                        onClick={() => setNewProgramIds([])}
                        style={{ background: '#F8FAFC', color: '#64748b', border: '2px solid #E2E8F0', borderRadius: 10, padding: '4px 14px', fontWeight: 800, fontSize: '0.78rem', cursor: 'pointer' }}
                      >
                        ✕ {lang === 'ja' ? '全て解除' : 'Deselect All'}
                      </button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {programs.map(p => {
                        const checked = newProgramIds.includes(p.id);
                        return (
                          <div key={p.id}
                            onClick={() => toggleAdminProgram(p.id)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 10,
                              padding: '0.6rem 1rem', borderRadius: 12,
                              border: `2px solid ${checked ? 'var(--orange)' : '#E2E8F0'}`,
                              background: checked ? '#FFF0E0' : 'white',
                              cursor: 'pointer', fontWeight: 700, fontSize: '0.88rem',
                              transition: 'all 0.15s', userSelect: 'none',
                            }}
                          >
                            <div style={{
                              width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                              background: checked ? 'var(--orange)' : 'white',
                              border: `2px solid ${checked ? 'var(--orange)' : '#CBD5E1'}`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              transition: 'all 0.15s',
                            }}>
                              {checked && <span style={{ color: 'white', fontSize: '0.75rem', fontWeight: 900, lineHeight: 1 }}>✓</span>}
                            </div>
                            <span style={{ fontSize: '1.1rem' }}>{p.icon || '📚'}</span>
                            <span style={{ color: checked ? 'var(--orange)' : '#1e293b' }}>
                              {lang === 'ja' ? (p.name_ja || p.name) : p.name}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    {/* Counter — always show */}
                    <div style={{ marginTop: 10, padding: '0.5rem 1rem', borderRadius: 10, background: newProgramIds.length > 0 ? '#FFF0E0' : '#F8FAFC', display: 'flex', alignItems: 'center' }}>
                      <span style={{ fontWeight: 800, fontSize: '0.82rem', color: newProgramIds.length > 0 ? 'var(--orange)' : '#94a3b8' }}>
                        {newProgramIds.length > 0
                          ? `✅ ${newProgramIds.length} / ${programs.length} ${lang === 'ja' ? 'プログラム選択済み' : 'programs selected'}`
                          : (lang === 'ja' ? '⚠️ プログラムを1つ以上選んでください' : '⚠️ Please select at least 1 program')}
                      </span>
                    </div>
                  </div>
                  <div className="mb-4">
                    <label style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--muted)', display: 'block', marginBottom: '0.4rem' }}>Assign to Teacher</label>
                    <select className="input-summer" value={newTeacherId} onChange={e => setNewTeacherId(e.target.value)}>
                      <option value="">Select teacher...</option>
                      {teachers.map(tc => <option key={tc.id} value={tc.id}>{tc.name}</option>)}
                    </select>
                  </div>
                  <button type="submit" className="btn-summer w-100">➕ {t.addStudent}</button>
                </form>

                {newStudentResult && (
                  <div className={`mt-3 p-3 ${newStudentResult.error ? 'alert-danger' : ''}`} style={{ background: newStudentResult.error ? '#FEE2E2' : '#DCFCE7', borderRadius: 16, fontWeight: 700 }}>
                    {newStudentResult.error ? newStudentResult.error : (
                      <>
                        <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>✅ Student created!</div>
                        <div style={{ fontSize: '1rem', color: 'var(--muted)' }}>Login Code:</div>
                        <div style={{ fontSize: '3rem', fontWeight: 900, color: 'var(--orange)', letterSpacing: '6px' }}>
                          {newStudentResult.login_code}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginTop: '0.3rem' }}>
                          Profile: /student/{newStudentResult.profile_slug}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Settings */}
            {tab === 'settings' && (
              <div className="card-summer p-4" style={{ maxWidth: 500 }}>
                <h5 style={{ fontWeight: 900, marginBottom: '1.5rem' }}>⚙️ {lang === 'ja' ? 'システム設定' : 'System Settings'}</h5>

                {/* Certificate threshold */}
                <div style={{ marginBottom: '2rem' }}>
                  <label style={{ fontWeight: 800, fontSize: '0.9rem', color: '#1e293b', display: 'block', marginBottom: '0.5rem' }}>
                    🎓 {lang === 'ja' ? '修了証解放ライン (%)' : 'Certificate Unlock Threshold (%)'}
                  </label>
                  <div style={{ fontSize: '0.8rem', color: 'var(--muted)', fontWeight: 600, marginBottom: '1rem' }}>
                    {lang === 'ja'
                      ? `生徒が全セッションの合計スコアのうち、この%以上を獲得すると修了証が解放されます。現在: ${certThreshold}%`
                      : `Students unlock their certificate when their total score reaches this percentage. Currently: ${certThreshold}%`}
                  </div>

                  {/* Big number display */}
                  <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                    <div style={{ fontSize: '4rem', fontWeight: 900, color: 'var(--orange)', lineHeight: 1 }}>{certThreshold}%</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--muted)', fontWeight: 700 }}>
                      {lang === 'ja' ? '修了証解放ライン' : 'Certificate threshold'}
                    </div>
                  </div>

                  <input
                    type="range"
                    min={50} max={100} step={1}
                    value={certThreshold}
                    onChange={e => setCertThreshold(parseInt(e.target.value))}
                    style={{ width: '100%', accentColor: 'var(--orange)', marginBottom: '0.5rem' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: 'var(--muted)', fontWeight: 700 }}>
                    <span>50%</span>
                    <span style={{ color: 'var(--orange)' }}>→ {certThreshold}%</span>
                    <span>100%</span>
                  </div>

                  {/* Quick presets */}
                  <div style={{ display: 'flex', gap: 8, marginTop: '1rem', flexWrap: 'wrap' }}>
                    {[70, 75, 80, 85, 87, 90, 95].map(v => (
                      <button key={v} onClick={() => setCertThreshold(v)} style={{ background: certThreshold === v ? 'var(--orange)' : '#F1F5F9', color: certThreshold === v ? 'white' : '#64748b', border: 'none', borderRadius: 8, padding: '4px 12px', fontWeight: 800, fontSize: '0.8rem', cursor: 'pointer' }}>
                        {v}%
                      </button>
                    ))}
                  </div>
                </div>

                <button onClick={saveSettings} className="btn-summer w-100">
                  {settingsSaved ? (lang === 'ja' ? '✓ 保存しました！' : '✓ Saved!') : (lang === 'ja' ? '💾 設定を保存' : '💾 Save Settings')}
                </button>

                <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#F8FAFC', borderRadius: 12, fontSize: '0.78rem', color: '#64748b', fontWeight: 600 }}>
                  <strong>ℹ️ {lang === 'ja' ? '修了証の仕組み' : 'How certificates work'}:</strong>
                  <br />
                  {lang === 'ja'
                    ? `先生が各セッションにスコア(1〜100%)をつけます。全セッションの合計スコアを最大スコアで割って、全体の%を計算します。この%が${certThreshold}%以上になると修了証が解放されます。`
                    : `Teachers give a score (1–100%) for each session. The total earned score divided by the maximum possible score gives an overall percentage. When this reaches ${certThreshold}%, the certificate unlocks.`}
                </div>
              </div>
            )}

            {/* Invite teacher */}
            {tab === 'invite' && (
              <div className="card-summer p-4" style={{ maxWidth: 500 }}>
                <h5 style={{ fontWeight: 900, marginBottom: '1.5rem' }}>📧 {t.inviteTeacher}</h5>
                <form onSubmit={inviteTeacher}>
                  <div className="mb-4">
                    <label style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--muted)', display: 'block', marginBottom: '0.4rem' }}>{t.teacherEmail}</label>
                    <input type="email" className="input-summer" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} required />
                  </div>
                  <button type="submit" className="btn-summer w-100">📧 {t.sendInvite}</button>
                </form>

                {inviteResult && (
                  <div className={`mt-3 p-3 alert ${inviteResult.error ? 'alert-danger' : 'alert-success'}`} style={{ borderRadius: 16, fontWeight: 700 }}>
                    {inviteResult.error ? inviteResult.error : (
                      <>
                        <div>✅ Invitation created!</div>
                        <div style={{ fontSize: '0.8rem', marginTop: '0.5rem', wordBreak: 'break-all' }}>
                          Invite URL: {inviteResult.invite_url}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>Token: {inviteResult.token}</div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Breakdown Modal */}
      {breakdownStudent && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', backdropFilter: 'blur(6px)' }}>
          <div style={{ background: 'white', borderRadius: 24, width: '100%', maxWidth: 560, padding: '2rem', boxShadow: '0 30px 80px rgba(0,0,0,0.25)', maxHeight: '88vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
              <div>
                <h5 style={{ fontWeight: 900, margin: 0 }}>📊 {breakdownStudent.name}</h5>
                <div style={{ fontSize: '0.78rem', color: 'var(--muted)', fontWeight: 600, marginTop: 2 }}>
                  {lang === 'ja' ? '日別スコア内訳' : 'Score breakdown by day'}
                </div>
              </div>
              <button onClick={() => setBreakdownStudent(null)} style={{ background: '#F1F5F9', border: 'none', borderRadius: 10, padding: '6px 12px', fontWeight: 800, fontSize: '1rem', cursor: 'pointer', color: '#64748b' }}>✕</button>
            </div>

            {breakdownLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem', fontSize: '1.5rem' }}>⭐</div>
            ) : breakdownData.length === 0 ? (
              <div style={{ color: 'var(--muted)', fontWeight: 600, textAlign: 'center', padding: '2rem' }}>
                {lang === 'ja' ? 'スタンプがまだありません' : 'No stamps recorded yet'}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                {breakdownData.map((prog, pi) => {
                  const avg = Math.round(prog.days.reduce((sum, d) => sum + d.score, 0) / prog.days.length);
                  return (
                    <div key={pi}>
                      {/* Program header */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <div style={{ fontWeight: 800, fontSize: '0.9rem' }}>
                          {prog.icon} {lang === 'ja' ? (prog.name_ja || prog.name) : prog.name}
                        </div>
                        <div style={{ fontSize: '0.78rem', fontWeight: 700, color: avg >= 80 ? '#16a34a' : avg >= 60 ? '#D97706' : '#DC2626' }}>
                          {lang === 'ja' ? '平均' : 'Avg'}: {avg}%
                        </div>
                      </div>
                      {/* Day rows */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                        {prog.days.map((d, di) => (
                          <div key={di} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 110, fontSize: '0.75rem', fontWeight: 700, color: '#64748b', flexShrink: 0 }}>
                              Day {d.day} · {d.session}
                            </div>
                            <div style={{ flex: 1, height: 10, background: '#F1F5F9', borderRadius: 5, overflow: 'hidden' }}>
                              <div style={{
                                height: '100%',
                                width: `${d.score}%`,
                                background: d.score >= 80 ? 'linear-gradient(90deg,#22c55e,#16a34a)' : d.score >= 60 ? 'linear-gradient(90deg,#f59e0b,#d97706)' : 'linear-gradient(90deg,#f87171,#dc2626)',
                                borderRadius: 5,
                                transition: 'width 0.4s',
                              }} />
                            </div>
                            <div style={{ width: 38, textAlign: 'right', fontSize: '0.78rem', fontWeight: 800, color: d.score >= 80 ? '#16a34a' : d.score >= 60 ? '#D97706' : '#DC2626', flexShrink: 0 }}>
                              {d.score}%
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}

                {/* Overall summary */}
                <div style={{ marginTop: '0.5rem', padding: '1rem', background: '#F8FAFC', borderRadius: 14, display: 'flex', gap: 20, justifyContent: 'center', flexWrap: 'wrap' }}>
                  {[
                    { label: lang === 'ja' ? '総スタンプ' : 'Total Stamps', val: breakdownData.reduce((s, p) => s + p.days.length, 0) + ' ⭐' },
                    { label: lang === 'ja' ? 'プログラム数' : 'Programs', val: breakdownData.length + ' 📚' },
                    { label: lang === 'ja' ? '全体平均' : 'Overall Avg', val: Math.round(breakdownData.flatMap(p => p.days).reduce((s, d) => s + d.score, 0) / Math.max(1, breakdownData.flatMap(p => p.days).length)) + '%' },
                  ].map((item, i) => (
                    <div key={i} style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '1.3rem', fontWeight: 900, color: 'var(--orange)' }}>{item.val}</div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--muted)', fontWeight: 700 }}>{item.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editUser && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', backdropFilter: 'blur(6px)' }}>
          <div style={{ background: 'white', borderRadius: 24, width: '100%', maxWidth: 420, padding: '2rem', boxShadow: '0 30px 80px rgba(0,0,0,0.25)' }}>
            <h5 style={{ fontWeight: 900, marginBottom: '0.5rem' }}>
              ✏️ {lang === 'ja' ? '編集' : 'Edit'} {editUser.role === 'student' ? '🎒' : '👩‍🏫'} {editUser.name}
            </h5>
            <div style={{ fontSize: '0.78rem', color: 'var(--muted)', fontWeight: 600, marginBottom: '1.5rem' }}>
              {editUser.role === 'student' ? (lang === 'ja' ? '生徒' : 'Student') : (lang === 'ja' ? '先生' : 'Teacher')}
            </div>
            <div className="mb-3">
              <label style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--muted)', display: 'block', marginBottom: '0.4rem' }}>Name (English)</label>
              <input className="input-summer" value={editUser.name} onChange={e => setEditUser(u => ({ ...u, name: e.target.value }))} />
            </div>
            <div className="mb-4">
              <label style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--muted)', display: 'block', marginBottom: '0.4rem' }}>名前（日本語）</label>
              <input className="input-summer" value={editUser.name_ja} onChange={e => setEditUser(u => ({ ...u, name_ja: e.target.value }))} />
            </div>
            {editMsg && <div style={{ marginBottom: '1rem', fontWeight: 700, color: editMsg.startsWith('✅') ? '#16a34a' : '#dc2626' }}>{editMsg}</div>}
            <div className="d-flex gap-2">
              <button className="btn-summer flex-fill" onClick={saveEditUser}>{lang === 'ja' ? '💾 保存' : '💾 Save'}</button>
              <button className="btn-outline-summer flex-fill" onClick={() => { setEditUser(null); setEditMsg(''); }}>✕</button>
            </div>
            <div style={{ marginTop: '1rem', padding: '0.75rem 1rem', background: '#FEF2F2', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 700, fontSize: '0.82rem', color: '#ef4444' }}>🗑️ {lang === 'ja' ? '削除する' : 'Delete permanently'}</span>
              <button onClick={() => { deleteUser(editUser); setEditUser(null); }}
                style={{ background: '#ef4444', color: 'white', border: 'none', borderRadius: 8, padding: '4px 14px', fontWeight: 800, fontSize: '0.78rem', cursor: 'pointer' }}>
                {lang === 'ja' ? '削除' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!confirmDelete}
        title={lang === 'ja' ? '削除の確認' : 'Confirm Delete'}
        message={confirmDelete ? (lang === 'ja'
          ? `「${confirmDelete.name}」を削除しますか？この操作は元に戻せません。`
          : `Delete "${confirmDelete.name}"? This cannot be undone.`)
          : ''}
        confirmLabel={lang === 'ja' ? '削除' : 'Delete'}
        cancelLabel={lang === 'ja' ? 'キャンセル' : 'Cancel'}
        danger
        onConfirm={confirmDeleteUser}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}
